import { Injectable } from '@angular/core';
import { AppConfigService } from './app-config.service';

const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'MarkaziaRefreshToken';
const USER_KEY = 'currentUser';

@Injectable({
  providedIn: 'root',
})
export class TokenService {

  constructor(private appConfig: AppConfigService) { }
  saveToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }

  get token(): string {
    let accessToken = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || '';
    if (!accessToken && !this.appConfig.production && this.appConfig.bypassAuth) {
      accessToken = this.appConfig.generalToken;
    }
    return accessToken;
  }

  setToken(accessToken: string): void {
    localStorage.setItem(TOKEN_KEY, accessToken);
  }

  setRefreshToken(token: string | null): void {
    if (token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  }

  decodeToken(): any | null {
    const token = this.token;
    if (!token) return null;

    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload);
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Invalid token format', error);
      return null;
    }
  }

  getFullName(): string {
    const decoded = this.decodeToken();
    return decoded?.unique_name || '';
  }

  getFirstAndLastName(): { firstName: string; lastName: string } {
    const fullName = this.getFullName().trim();
    if (!fullName) return { firstName: '', lastName: '' };

    const parts = fullName.split(' ').filter(Boolean);
    return {
      firstName: parts[0],
      lastName: parts.length > 1 ? parts.slice(1).join(' ') : '',
    };
  }

  private writeCookieToken(name: string, value: string | null): void {
    if (typeof document === 'undefined') return;

    const parts: string[] = [];

    if (value) {
      const json = `{"value":"${value}"}`;
      parts.push(`${name}=${json}`);
      parts.push('Max-Age=86400');
    } else {
      parts.push(`${name}=`);
      parts.push('expires=Thu, 01 Jan 1970 00:00:00 GMT', 'Max-Age=0');
    }

    parts.push('Path=/');
    parts.push('SameSite=Lax');

    const hostname = location.hostname;
    if (hostname.includes('markaziahub')) {
      parts.push('Domain=.markaziahub.com');
    }

    if (location.protocol === 'https:') {
      parts.push('Secure');
    }

    document.cookie = parts.join('; ');
  }

  private readCookieToken(name: string): string | null {
    if (typeof document === 'undefined' || !document.cookie) return null;

    const prefix = name + '=';
    const found = document.cookie.split('; ').find(c => c.startsWith(prefix));
    if (!found) return null;

    const raw = found.substring(prefix.length);
    try {
      const obj = JSON.parse(raw);
      if (typeof obj?.value === 'string' && obj.value.trim()) return obj.value;
    } catch { }
    try {
      const decoded = decodeURIComponent(raw);
      const obj = JSON.parse(decoded);
      if (typeof obj?.value === 'string' && obj.value.trim()) return obj.value;
    } catch { }

    return null;
  }

  get ssoAccessToken(): string | null {
    return this.readCookieToken('MarkaziaAccessToken');
  }

  get ssoRefreshToken(): string | null {
    return this.readCookieToken('MarkaziaRefreshToken');
  }

  ssoAccessTokenSet(token: string | null): void {
    this.writeCookieToken('MarkaziaAccessToken', token);
  }

  ssoRefreshTokenSet(token: string | null): void {
    this.writeCookieToken('MarkaziaRefreshToken', token);
  }

  get isSingleSignOnMode(): boolean {
    const localToken = this.readTokenFromStorage();
    const ssoAccess = this.ssoAccessToken;
    const ssoRefresh = this.ssoRefreshToken;
    return !localToken && !!ssoAccess && !!ssoRefresh;
  }

  get effectiveAccessToken(): string {
    return this.readTokenFromStorage() || this.ssoAccessToken || '';
  }

  private readTokenFromStorage(): string {
    return localStorage.getItem(TOKEN_KEY) || '';
  }

  saveUser(user: any): void {
    try {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch { }
  }

  get getUser(): any {
    const cached = localStorage.getItem(USER_KEY);

    const employeeCache = localStorage.getItem('employeeData');
    let employeeInfo: any = null;
    if (employeeCache) {
      try {
        const parsed = JSON.parse(employeeCache);
        employeeInfo = parsed?.Data ?? null;
      } catch {
        console.warn('Invalid employeeData format in localStorage');
      }
    }

    if (cached) {
      try {
        const obj = JSON.parse(cached);

        if (employeeInfo?.EmployeeID) {
          obj.EmployeeID = employeeInfo.EmployeeID;
          obj.nameid = employeeInfo.EmployeeID;
          obj.UserID = employeeInfo.EmployeeID;
        }

        if (obj.UserID || obj.nameid) return { ...obj, ...this.portalPatch(obj) };
      } catch (err) {
        console.error('Error parsing cached user', err);
      }
    }

    const token = this.token || this.ssoAccessToken;
    if (token) {
      try {
        const payloadBase64Url = token.split('.')[1] ?? '';
        const b64 = payloadBase64Url
          .replace(/-/g, '+')
          .replace(/_/g, '/')
          .padEnd(Math.ceil(payloadBase64Url.length / 4) * 4, '=');

        const payloadJson = atob(b64);
        const p = JSON.parse(payloadJson);
        console.log('Decoded JWT payload:', p);

        const id = this.extractUserId(p);

        let role: any = null;
        let branch: any = null;

        try {
          role = typeof p.Role === 'string' ? JSON.parse(p.Role) : p.Role;
        } catch { }
        try {
          branch = typeof p.Branch === 'string' ? JSON.parse(p.Branch) : p.Branch;
        } catch { }

        const fullName =
          p.fullName ??
          p.unique_name ??
          p.name ??
          p.UserTitleEn ??
          p['given_name'] ??
          '';

        const employeeID = employeeInfo?.EmployeeID ?? null;

        return {
          nameid: employeeID ?? id,
          UserID: employeeID ?? id,
          EmployeeID: employeeID ?? null,
          unique_name: p.unique_name ?? p.name ?? '',
          fullName,
          mobile:
            p['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/mobilephone'] ??
            employeeInfo?.MobileNumber ??
            '',
          email: employeeInfo?.Email ?? '',
          branchId: p.BranchId ?? branch?.BranchId ?? null,
          branchName: branch?.BranchName ?? null,
          roleId: role?.RoleId ?? null,
          roleName: role?.Name ?? null,
          city: p.City ?? '',
          register: p.Register ?? '',
          register2: p.Register2 ?? '',
          issuedAt: p.iat ?? null,
          expiresAt: p.exp ?? null,
          notBefore: p.nbf ?? null,
          rawPayload: p,
          ...this.portalPatch({})
        };
      } catch (err) {
        console.error('Error decoding token', err);
      }
    }

    return {};
  }

  saveEmployeeInfo(data: any): void {
    if (!data) return;
    try {
      localStorage.setItem('employeeData', JSON.stringify(data));
    } catch (err) {
      console.error('Error saving employee info', err);
    }
  }

  private extractUserId(payload: any): string | null {
    if (!payload) return null;

    if (payload.EmployeeID) return String(payload.EmployeeID);
    if (payload.EmpNo) return String(payload.EmpNo);

    const keys = [
      'nameid',
      'sid',
      'sub',
      'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid',
      'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'
    ];

    for (const k of keys) {
      if (payload?.[k]) return String(payload[k]);
    }

    return null;
  }

  private portalPatch(src: any) {
    const pid = localStorage.getItem('userPortal');
    return pid ? { portalID: pid } : src;
  }

  get currentUserId(): string | null {
    const u = this.getUser || {};
    return u?.nameid ?? u?.UserID ?? null;
  }

  clearTokens(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    this.writeCookieToken('MarkaziaAccessToken', null);
    this.writeCookieToken('MarkaziaRefreshToken', null);
  }

  signOut(): void {
    this.clearTokens();
  }

  get firstName(): string {
    const user = this.getUser;
    if (user?.FirstName) return user.FirstName;
    if (!user?.fullName) return '';
    return user.fullName.split(' ')[0] || '';
  }

  get lastName(): string {
    const user = this.getUser;
    if (user?.LastName) return user.LastName || user.SecondName || '';
    if (!user?.fullName) return '';
    const parts = user.fullName.split(' ');
    return parts.length > 1 ? parts.slice(1).join(' ') : '';
  }
}
